import { Injectable } from '@angular/core'
import { HttpEventType } from '@angular/common/http'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { NotificationType } from 'angular2-notifications'
import { of } from 'rxjs'
import { catchError, delay, filter, map, switchMap, tap } from 'rxjs/operators'
import { AppFileDownloadService } from '../../services/app-file-download.service'
import { AppNotificationService } from '../../services/app-notification.service'
import { AppSpinnerService } from '../../services/app-spinner.service'
import { BudgetCategoryDOtherCostsResponse } from '../../../features/feature-budget/models/interfaces/budget-category-d-data.interface'
import { BudgetCostsPerWpResponse } from '../../../features/feature-budget/models/interfaces/budget-costs-per-wp.interface'
import { BudgetEffort } from '../../../features/feature-budget/models/interfaces/budget-effort.interface'
import { BudgetIncomeResponse } from '../../../features/feature-budget/models/interfaces/budget-income.interface'
import { BudgetJustificationOfCostsResult } from '../../../features/feature-budget/models/interfaces/budget-justification-of-costs.interface'
import { BudgetJustificationPartner } from '../../../features/feature-budget/models/interfaces/budget-justification-partner.interface'
import { BudgetSalary } from '../../../features/feature-budget/models/interfaces/budget-salary.interface'
import { BudgetStaffEffortResponse } from '../../../features/feature-budget/models/interfaces/budget-staff-effort.interface'
import { BudgetWorkPackage } from '../../../features/feature-budget/models/interfaces/budget-workpackage.interface'
import { BudgetWpWeightResponse } from '../../../features/feature-budget/models/interfaces/budget-wp-weight.interface'
import { BudgetService } from './budget.service'
import { AppErrorHandlerService } from '../../services/app-error-handler/app-error-handler.service'
import * as BudgetActions from './budget.actions'

@Injectable()
export class BudgetEffects {
   loadSalary$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadSalary),
         switchMap((action) =>
            this.budgetService.getSalary().pipe(
               map((response: BudgetSalary) => BudgetActions.loadSalarySuccess({ response })),
               catchError((error) => of(BudgetActions.loadSalaryFailed({ error })))
            )
         )
      )
   )

   updateSalary$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.updateSalary),
         switchMap((action) =>
            this.budgetService.updateSalary(action.data).pipe(
               map((response: BudgetSalary) => BudgetActions.updateSalarySuccess({ response })),
               catchError((error) => of(BudgetActions.updateSalaryFailed({ error })))
            )
         )
      )
   )

   loadEffort$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadEffort),
         switchMap((action) =>
            this.budgetService.getEffort().pipe(
               map((response: BudgetEffort) => BudgetActions.loadEffortSuccess({ response })),
               catchError((error) => of(BudgetActions.loadEffortFailed({ error })))
            )
         )
      )
   )

   updateEffort$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.updateEffort),
         switchMap((action) =>
            this.budgetService.updateEffort(action.data).pipe(
               map((response: BudgetEffort) => BudgetActions.updateEffortSuccess({ response })),
               catchError((error) => of(BudgetActions.updateEffortFailed({ error })))
            )
         )
      )
   )

   loadIncome$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadIncome),
         switchMap((action) =>
            this.budgetService.getIncome().pipe(
               map((response: BudgetIncomeResponse) => BudgetActions.loadIncomeSuccess({ response: response.partners })),
               catchError((error) => of(BudgetActions.loadIncomeFailed({ error })))
            )
         )
      )
   )

   updateIncome$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.updateIncome),
         switchMap((action) =>
            this.budgetService.updateIncome(action.data).pipe(
               map((response: BudgetIncomeResponse) => BudgetActions.updateIncomeSuccess({ response: response.partners })),
               catchError((error) => of(BudgetActions.updateIncomeFailed({ error })))
            )
         )
      )
   )

   loadStaffEffort$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadStaffEffort),
         switchMap((action) =>
            this.budgetService.getStaffEffort().pipe(
               map((response: BudgetStaffEffortResponse) => BudgetActions.loadStaffEffortSuccess({ response: response.staff_effort.data })),
               catchError((error) => of(BudgetActions.loadStaffEffortFailed({ error })))
            )
         )
      )
   )

   exportStaffEffort$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.exportStaffEffort),
         switchMap((action) =>
            this.budgetService.exportStaffEffort(action.fileType).pipe(
               tap((response) => this.fileDownloadService.save(response, 'PMP_Budget_EFFORT')),
               map((response) => BudgetActions.exportStaffEffortSuccess({ response })),
               catchError((error) => of(BudgetActions.exportStaffEffortFailed({ error })))
            )
         )
      )
   )

   exportPpsForms$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.exportPpsForms),
         switchMap((action) =>
            this.budgetService.exportPpsForms(action.fileType).pipe(
               tap((response) => this.fileDownloadService.save(response, 'PMP_Budget_PPS_FORM')),
               map((response) => BudgetActions.exportPpsFormsSuccess({ response })),
               catchError((error) => of(BudgetActions.exportPpsFormsFailed({ error })))
            )
         )
      )
   )

   loadWpWeight$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadWpWeight),
         switchMap((action) =>
            this.budgetService.getWpWeight().pipe(
               map((response: BudgetWpWeightResponse) => BudgetActions.loadWpWeightSuccess({ response: response.wp_weight.data })),
               catchError((error) => of(BudgetActions.loadWpWeightFailed({ error })))
            )
         )
      )
   )

   exportWpWeight$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.exportWpWeight),
         switchMap((action) =>
            this.budgetService.exportWpWeight(action.fileType).pipe(
               tap((response) => this.fileDownloadService.save(response, 'PMP_Budget_WP_WEIGHT')),
               map((response) => BudgetActions.exportWpWeightSuccess({ response })),
               catchError((error) => of(BudgetActions.exportWpWeightFailed({ error })))
            )
         )
      )
   )

   loadWorkPackage$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadWorkPackage),
         switchMap((action) =>
            this.budgetService.getWorkPackage().pipe(
               map((response: BudgetWorkPackage[]) => BudgetActions.loadWorkPackageSuccess({ response })),
               catchError((error) => of(BudgetActions.loadWorkPackageFailed({ error })))
            )
         )
      )
   )

   loadCostsWp$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadCostsWp),
         switchMap((action) =>
            this.budgetService.getCostsWp(action.workPackageId).pipe(
               map((response: BudgetCostsPerWpResponse) => BudgetActions.loadCostsWpSuccess({ response: response.partners })),
               catchError((error) => of(BudgetActions.loadCostsWpFailed({ error })))
            )
         )
      )
   )

   updateCostsWp$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.updateCostsWp),
         switchMap((action) =>
            this.budgetService.updateCostsWp(action.workPackageId, action.data).pipe(
               map((response: BudgetCostsPerWpResponse) => BudgetActions.updateCostsWpSuccess({ response: response.partners })),
               catchError((error) => of(BudgetActions.updateCostsWpFailed({ error })))
            )
         )
      )
   )

   loadPartner$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadPartner),
         switchMap((action) =>
            this.budgetService.getPartner().pipe(
               map((response: BudgetJustificationPartner[]) => BudgetActions.loadPartnerSuccess({ response })),
               catchError((error) => of(BudgetActions.loadPartnerFailed({ error })))
            )
         )
      )
   )

   updatePartnerStatus$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.updatePartnerStatus),
         switchMap((action) =>
            this.budgetService.updatePartnerStatus(action.partnerId, action.status).pipe(
               map(() => BudgetActions.updatePartnerStatusSuccess()),
               catchError((error) => of(BudgetActions.updatePartnerStatusFailed({ error })))
            )
         )
      )
   )

   loadBudgetJustificationOfCosts$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadBudgetJustificationOfCosts),
         switchMap((action) =>
            this.budgetService.getBudgetJustificationOfCosts(action.partnerId).pipe(
               map((response: BudgetJustificationOfCostsResult) => BudgetActions.loadBudgetJustificationOfCostsSuccess({ response })),
               catchError((error) => of(BudgetActions.loadBudgetJustificationOfCostsFailed({ error })))
            )
         )
      )
   )

   updateCostsJustif$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.updateBudgetJustificationOfCosts),
         switchMap((action) =>
            this.budgetService.updateBudgetJustificationOfCosts(action.partnerId, action.data).pipe(
               map(() => BudgetActions.updateBudgetJustificationOfCostsSuccess()),
               catchError((error) => of(BudgetActions.updateBudgetJustificationOfCostsFailed({ error })))
            )
         )
      )
   )

   exportCostsJustif$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.exportCostsJustif),
         switchMap((action) =>
            this.budgetService.exportCostsJustif(action.fileType).pipe(
               tap((response) => this.fileDownloadService.save(response, 'PMP_Budget_JUSTIFY_COST')),
               map((response) => BudgetActions.exportCostsJustifSuccess({ response })),
               catchError((error) => of(BudgetActions.exportCostsJustifFailed({ error })))
            )
         )
      )
   )

   closeExportProgress$ = createEffect(() =>
      this.actions$.pipe(
         ofType(
            BudgetActions.exportStaffEffortSuccess,
            BudgetActions.exportPpsFormsSuccess,
            BudgetActions.exportWpWeightSuccess,
            BudgetActions.exportCostsJustifSuccess
         ),
         filter(({ response }) => response.type === HttpEventType.Response),
         delay(1000),
         map(() => BudgetActions.closeExportProgress())
      )
   )

   loadBudgetCategoryDOtherCosts$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.loadBudgetCategoryDOtherCosts),
         switchMap((action) =>
            this.budgetService.getBudgetCategoryDOtherCosts(action.workpackageId).pipe(
               map((response: BudgetCategoryDOtherCostsResponse) => BudgetActions.loadBudgetCategoryDOtherCostsSuccess({ response: response.partners })),
               catchError((error) => of(BudgetActions.loadBudgetCategoryDOtherCostsFailed({ error })))
            )
         )
      )
   )

   updateBudgetCategoryDOtherCosts$ = createEffect(() =>
      this.actions$.pipe(
         ofType(BudgetActions.putBudgetCategoryDOtherCosts),
         switchMap((action) =>
            this.budgetService.putBudgetCategoryDOtherCosts(action.workpackageId, action.data).pipe(
               map(() => BudgetActions.putBudgetCategoryDOtherCostsSuccess()),
               catchError((error) => of(BudgetActions.putBudgetCategoryDOtherCostsFailed({ error })))
            )
         )
      )
   )

   constructor(
      private actions$: Actions,
      private budgetService: BudgetService,
      private spinnerService: AppSpinnerService,
      private notificationService: AppNotificationService,
      private errorHandlerService: AppErrorHandlerService,
      private fileDownloadService: AppFileDownloadService
   ) {}

   private _actionsForSpinnerHide = [
      BudgetActions.loadWorkPackageSuccess,
      BudgetActions.loadPartnerSuccess,
      BudgetActions.updateSalarySuccess,
      BudgetActions.updateEffortSuccess,
      BudgetActions.updateIncomeSuccess,
      BudgetActions.updateCostsWpSuccess,
      BudgetActions.updateBudgetJustificationOfCostsSuccess,
      BudgetActions.updateSalaryFailed,
      BudgetActions.updateEffortFailed,
      BudgetActions.updateIncomeFailed,
      BudgetActions.updateCostsWpFailed,
      BudgetActions.updateBudgetJustificationOfCostsFailed,
      BudgetActions.loadWorkPackageFailed,
      BudgetActions.loadCostsWpFailed,
      BudgetActions.loadPartnerFailed,
      BudgetActions.loadSalarySuccess,
      BudgetActions.loadSalaryFailed,
      BudgetActions.loadEffortSuccess,
      BudgetActions.loadEffortFailed,
      BudgetActions.loadIncomeSuccess,
      BudgetActions.loadIncomeFailed,
      BudgetActions.loadStaffEffortSuccess,
      BudgetActions.loadStaffEffortFailed,
      BudgetActions.loadWpWeightSuccess,
      BudgetActions.loadWpWeightFailed,
      BudgetActions.loadBudgetJustificationOfCostsSuccess,
      BudgetActions.loadBudgetJustificationOfCostsFailed,
      BudgetActions.updatePartnerStatusSuccess,
      BudgetActions.updatePartnerStatusFailed,
      BudgetActions.loadBudgetCategoryDOtherCostsSuccess,
      BudgetActions.loadBudgetCategoryDOtherCostsFailed,
      BudgetActions.putBudgetCategoryDOtherCostsSuccess,
      BudgetActions.putBudgetCategoryDOtherCostsFailed
   ]

   listenAllSpinnerHideAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerHide),
            map(() => this.spinnerService.spinnerHide())
         ),
      { dispatch: false }
   )

   private _actionsForSpinnerShow = [
      BudgetActions.updateSalary,
      BudgetActions.updateEffort,
      BudgetActions.updateIncome,
      BudgetActions.loadWorkPackage,
      BudgetActions.updateCostsWp,
      BudgetActions.loadPartner,
      BudgetActions.updateBudgetJustificationOfCosts,
      BudgetActions.loadSalary,
      BudgetActions.loadEffort,
      BudgetActions.loadIncome,
      BudgetActions.loadStaffEffort,
      BudgetActions.loadWpWeight,
      BudgetActions.loadBudgetJustificationOfCosts,
      BudgetActions.updatePartnerStatus,
      BudgetActions.loadBudgetCategoryDOtherCosts,
      BudgetActions.putBudgetCategoryDOtherCosts
   ]

   listenAllSpinnerShowAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerShow),
            map(() => this.spinnerService.spinnerShow())
         ),
      { dispatch: false }
   )

   private _actionsFailedNotification = [
      BudgetActions.updateSalaryFailed,
      BudgetActions.updateEffortFailed,
      BudgetActions.updateIncomeFailed,
      BudgetActions.updateCostsWpFailed,
      BudgetActions.updateBudgetJustificationOfCostsFailed,
      BudgetActions.loadSalaryFailed,
      BudgetActions.loadEffortFailed,
      BudgetActions.loadIncomeFailed,
      BudgetActions.loadStaffEffortFailed,
      BudgetActions.loadWpWeightFailed,
      BudgetActions.loadWorkPackageFailed,
      BudgetActions.loadCostsWpFailed,
      BudgetActions.loadPartnerFailed,
      BudgetActions.updatePartnerStatusFailed,
      BudgetActions.loadBudgetJustificationOfCostsFailed,
      BudgetActions.loadBudgetCategoryDOtherCostsFailed,
      BudgetActions.putBudgetCategoryDOtherCostsFailed,
      BudgetActions.exportStaffEffortFailed,
      BudgetActions.exportPpsFormsFailed,
      BudgetActions.exportWpWeightFailed,
      BudgetActions.exportCostsJustifFailed
   ]

   listenAllFailedAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsFailedNotification),
            map((errorResponse) => this.errorHandlerService.handleError(errorResponse.error))
         ),
      { dispatch: false }
   )

   private _actionsSuccessNotification = [
      BudgetActions.updateSalarySuccess,
      BudgetActions.updateEffortSuccess,
      BudgetActions.updateIncomeSuccess,
      BudgetActions.updateCostsWpSuccess,
      BudgetActions.updateBudgetJustificationOfCostsSuccess,
      BudgetActions.updatePartnerStatusSuccess,
      BudgetActions.putBudgetCategoryDOtherCostsSuccess
   ]

   listenAllSuccessAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsSuccessNotification),
            map(() => this.notificationService.notify(NotificationType.Success))
         ),
      { dispatch: false }
   )
}
